import { type SubmitHandler, useForm } from 'react-hook-form';

import type { Space } from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Form,
    Heading,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../state';

interface UpdateSpaceTitleDialogProps {
    space: Space;
}

/**
 * Dialog to update a space's title.
 */
export function UpdateSpaceTitleDialog(props: UpdateSpaceTitleDialogProps) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Rename space</Heading>
                <Description>
                    This is the title which will be used to reference your space throughout the
                    organization.
                </Description>
            </DialogHeader>
            <UpdateSpaceTitleDialogForm {...props} />
        </Dialog>
    );
}

type FormInputs = {
    title: string;
};

function UpdateSpaceTitleDialogForm(props: UpdateSpaceTitleDialogProps) {
    const { space } = props;

    const [isPending, startDialogAction] = useDialogAction();
    const form = useForm<FormInputs>({
        defaultValues: { title: space.title },
        disabled: isPending,
    });
    const onSubmit: SubmitHandler<FormInputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.spaces.updateSpaceById(space.id, {
                    title: data.title,
                });

                await close();
            }, 'Error while updating title');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="title">
                    <TextInput placeholder="Space title" autoFocus maxLength={50} />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Rename" />
            </DialogFooter>
        </Form>
    );
}
